import type {
  CopilotChatEventPayload,
  CopilotChatReference,
  CopilotCustomInstructions,
} from '@github-ui/copilot-chat-types'

export class OpenCopilotChatEvent extends Event {
  payload: CopilotChatEventPayload
  constructor(payload: CopilotChatEventPayload) {
    super('open-copilot-chat', {
      bubbles: false,
      cancelable: true,
    })

    this.payload = payload
  }

  get telemetrySource(): string {
    return (
      this.payload.telemetrySource ?? (this.payload.id ? `element ${this.payload.id}` : `blob ${this.payload.intent}`)
    )
  }
}

export class SearchCopilotEvent extends Event {
  content: string
  repoNwo: string
  constructor(content: string, repoNwo: string) {
    super('search-copilot-chat', {
      bubbles: false,
      cancelable: true,
    })

    this.content = content
    this.repoNwo = repoNwo
  }
}

export class AddCopilotChatReferenceEvent extends Event {
  reference: CopilotChatReference
  openPanel?: boolean
  id?: string
  constructor(reference: CopilotChatReference, openPanel: boolean = false, id?: string) {
    super('add-copilot-chat-reference', {
      bubbles: false,
      cancelable: true,
    })
    this.reference = reference
    this.openPanel = openPanel
    this.id = id
  }
}

export class SymbolChangedEvent extends Event {
  context: CopilotChatReference
  constructor(context: CopilotChatReference) {
    super('symbol-changed', {
      bubbles: false,
      cancelable: true,
    })

    this.context = context
  }
}

export interface UpdateCopilotSuggestionsContextPayload {
  suggestionsContext: string
  customInstructions?: CopilotCustomInstructions
}

export class UpdateCopilotSuggestionsContextEvent extends Event {
  payload: UpdateCopilotSuggestionsContextPayload
  constructor(payload: UpdateCopilotSuggestionsContextPayload) {
    super('update-copilot-suggestions-context', {
      bubbles: false,
      cancelable: true,
    })
    this.payload = payload
  }
}

export class PreloadCopilotChatEvent extends Event {
  constructor() {
    super('preload-copilot-chat', {
      bubbles: false,
      cancelable: true,
    })
  }
}

declare global {
  interface WindowEventMap {
    'open-copilot-chat': OpenCopilotChatEvent
    'add-copilot-chat-reference': AddCopilotChatReferenceEvent
    'search-copilot-chat': SearchCopilotEvent
    'symbol-changed': SymbolChangedEvent
    'update-copilot-suggestions-context': UpdateCopilotSuggestionsContextEvent
    'preload-copilot-chat': PreloadCopilotChatEvent
  }
}

export function logCopilotChatRouting(step: string, details: Record<string, unknown>): void {
  if (new URLSearchParams(window.location.search).get('copilot_chat_debug') !== '1') return

  // eslint-disable-next-line no-console
  console.info(`[Copilot chat routing] ${step}`, details)
}

function logOpenEvent(step: string, payload: CopilotChatEventPayload, event?: OpenCopilotChatEvent): void {
  logCopilotChatRouting(step, {
    id: payload.id,
    intent: payload.intent,
    source: payload.source,
    newThread: 'newThread' in payload ? payload.newThread : undefined,
    referenceCount: payload.references?.length ?? 0,
    defaultPrevented: event?.defaultPrevented,
  })
}

const openCopilotChatConsumerClaims = new Set<symbol>()
const openCopilotChatConsumerAvailabilityListeners = new Set<() => void>()

function notifyOpenCopilotChatConsumerAvailability(): void {
  for (const listener of openCopilotChatConsumerAvailabilityListeners) {
    listener()
  }
}

export function claimOpenCopilotChatConsumerAvailability(): () => void {
  const claim = Symbol()
  openCopilotChatConsumerClaims.add(claim)
  notifyOpenCopilotChatConsumerAvailability()

  return () => {
    if (!openCopilotChatConsumerClaims.delete(claim)) return
    notifyOpenCopilotChatConsumerAvailability()
  }
}

export function isOpenCopilotChatConsumerAvailable(): boolean {
  return openCopilotChatConsumerClaims.size > 0
}

export function subscribeOpenCopilotChatConsumerAvailability(listener: () => void): () => void {
  openCopilotChatConsumerAvailabilityListeners.add(listener)
  return () => {
    openCopilotChatConsumerAvailabilityListeners.delete(listener)
  }
}

export function publishOpenCopilotChat(payload: CopilotChatEventPayload): void {
  const event = new OpenCopilotChatEvent(payload)
  logOpenEvent('publishing open event', payload)
  window.dispatchEvent(event)
  logOpenEvent('open event dispatch completed', payload, event)
}

export function publishAddCopilotChatReference(
  payload: CopilotChatReference,
  openPanel: boolean = false,
  id?: string,
): void {
  window.dispatchEvent(new AddCopilotChatReferenceEvent(payload, openPanel, id))
}

export function subscribeOpenCopilotChat(listener: (e: OpenCopilotChatEvent) => void): () => void {
  window.addEventListener('open-copilot-chat', listener)

  return () => {
    window.removeEventListener('open-copilot-chat', listener)
  }
}

export function subscribeAddCopilotChatReference(listener: (e: AddCopilotChatReferenceEvent) => void): () => void {
  window.addEventListener('add-copilot-chat-reference', listener)
  return () => {
    window.removeEventListener('add-copilot-chat-reference', listener)
  }
}

export function subscribeSearchCopilot(listener: (e: SearchCopilotEvent) => void): () => void {
  window.addEventListener('search-copilot-chat', listener)
  return () => {
    window.removeEventListener('search-copilot-chat', listener)
  }
}

export function subscribeSymbolChanged(listener: (e: SymbolChangedEvent) => void): () => void {
  window.addEventListener('symbol-changed', listener)

  return () => {
    window.removeEventListener('symbol-changed', listener)
  }
}

export function publishUpdateCopilotSuggestionsContext(payload: UpdateCopilotSuggestionsContextPayload): void {
  window.dispatchEvent(new UpdateCopilotSuggestionsContextEvent(payload))
}

export function subscribeUpdateCopilotSuggestionsContext(
  listener: (e: UpdateCopilotSuggestionsContextEvent) => void,
): () => void {
  window.addEventListener('update-copilot-suggestions-context', listener)
  return () => {
    window.removeEventListener('update-copilot-suggestions-context', listener)
  }
}

export function publishPreloadCopilotChat(): void {
  window.dispatchEvent(new PreloadCopilotChatEvent())
}

export function subscribePreloadCopilotChat(listener: (e: PreloadCopilotChatEvent) => void): () => void {
  window.addEventListener('preload-copilot-chat', listener)
  return () => {
    window.removeEventListener('preload-copilot-chat', listener)
  }
}
